import { toKebabCase } from './string.helper';
import {
  NotificationType,
  UserOrderNotificationSettings,
  UserOrderNotificationSettingsType,
} from '../types/database/dispatch-svc';

export const NotificationUrlMapper: Record<NotificationType, (params: any) => string> = {
  CLAIM_CREATE: ({ orderId }) => `/claims/view/${orderId}`,
  CLAIM_STATUS_CHANGE: ({ orderId }) => `/claims/view/${orderId}`,
  CLAIM_RESOLUTION: ({ orderId }) => `/claims/view/${orderId}`,
  CLAIM_MESSAGES: ({ orderId }) => `/claims/view/${orderId}`,
  INTERNAL_NOTES: ({ orderId }) => `/claims/view/${orderId}`,
  INTERNAL_NOTES_MENTION: ({ orderId }) => `/claims/view/${orderId}`,
  CLAIM_REVIEWS: ({ orderId }) => `/claims/view/${orderId}`,
  SOP_CREATE: ({ storeSlug, sopId }) => `/${storeSlug}/sops/${sopId}`,
  SOP_UPDATE: ({ storeSlug, sopId }) => `/${storeSlug}/sops/${sopId}`,
  MACRO_CREATE: ({ storeSlug, macroFolder }) => `/${storeSlug}/macros/${toKebabCase(macroFolder)}`,
  MACRO_UPDATE: ({ storeSlug, macroFolder }) => `/${storeSlug}/macros/${toKebabCase(macroFolder)}`,
  CLAIM_TAKEOVER_STALE: ({ orderId }) => `/claims/view/${orderId}`,
  CLAIM_TAKEOVER_RETURNED: ({ orderId }) => `/claims/view/${orderId}`,
};

/** "35m", "1h 10m", "2d 3h" — idle copy for the takeover watchdog, which now runs on a one-hour ladder. */
export function formatIdleMinutes(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total}m`;

  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  const remainder = total % 60;

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;

  return remainder > 0 ? `${hours}h ${remainder}m` : `${hours}h`;
}

/** Idle label for a watchdog payload; rows written before `minutesIdle` existed fall back to whole hours. */
export function takeoverIdleLabel(data: { minutesIdle?: number | null; hoursIdle: number }): string {
  return formatIdleMinutes(data.minutesIdle ?? data.hoursIdle * 60);
}

export const determineIsOrderNotification = (type: NotificationType): boolean =>
  type === NotificationType.CLAIM_CREATE ||
  type === NotificationType.CLAIM_STATUS_CHANGE ||
  type === NotificationType.CLAIM_RESOLUTION ||
  type === NotificationType.CLAIM_MESSAGES ||
  type === NotificationType.INTERNAL_NOTES ||
  type === NotificationType.INTERNAL_NOTES_MENTION ||
  type === NotificationType.CLAIM_REVIEWS;

export type CheckCanNotifyByOrderSettingsParams = Pick<
  UserOrderNotificationSettings,
  'type' | 'claimStatus' | 'customerMessages' | 'internalNotes'
>;

export const checkCanNotifyByOrderSettings = (
  type: NotificationType,
  orderSettings: CheckCanNotifyByOrderSettingsParams | null,
): boolean => {
  if (!orderSettings) {
    return false;
  }

  const { type: settingsType } = orderSettings;

  // Always allow internal notes mentions for non-subscribed users
  if (
    settingsType === UserOrderNotificationSettingsType.NOT_SUBSCRIBED &&
    type === NotificationType.INTERNAL_NOTES_MENTION
  ) {
    return true;
  }

  // Subscribed users receive all notifications
  if (settingsType === UserOrderNotificationSettingsType.SUBSCRIBED) {
    return true;
  }

  // Handle custom notification settings
  if (settingsType === UserOrderNotificationSettingsType.CUSTOM) {
    // Check claim status notifications
    if (orderSettings.claimStatus) {
      const claimStatusTypes = [
        NotificationType.CLAIM_CREATE,
        NotificationType.CLAIM_STATUS_CHANGE,
        NotificationType.CLAIM_RESOLUTION,
      ];
      if (claimStatusTypes.includes(type)) {
        return true;
      }
    }

    // Check customer message notifications
    if (orderSettings.customerMessages && type === NotificationType.CLAIM_MESSAGES) {
      return true;
    }

    // Check internal notes notifications
    if (orderSettings.internalNotes) {
      const internalNotesTypes = [NotificationType.INTERNAL_NOTES, NotificationType.INTERNAL_NOTES_MENTION];
      if (internalNotesTypes.includes(type)) {
        return true;
      }
    }
  }

  return false;
};
